import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { DateDifference } from "../models";

dayjs.extend(customParseFormat);

export const getUtcTicks = (): number => {
  const epochTicks = 621355968000000000; // Ticks at Unix epoch (1970-01-01T00:00:00Z)
  const ticksPerMillisecond = 10000;
  const now = Date.now(); // Current time in milliseconds since Unix epoch
  return epochTicks + now * ticksPerMillisecond;
};

export function getCurrentDateLongNoYear(): string {
  const date = new Date();

  const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
  const month = date.toLocaleDateString("en-US", { month: "long" });
  const day = date.getDate();

  const getOrdinalSuffix = (n: number): string => {
    if (n >= 11 && n <= 13) return `${n}th`;
    const lastDigit = n % 10;
    switch (lastDigit) {
      case 1:
        return `${n}st`;
      case 2:
        return `${n}nd`;
      case 3:
        return `${n}rd`;
      default:
        return `${n}th`;
    }
  };

  const dayWithSuffix = getOrdinalSuffix(day);

  return `${weekday}, ${month} ${dayWithSuffix}`;
}

export function parseDate(dateString?: string) {
  if (!dateString || dateString.trim().length === 0) return null;
  return new Date(dateString);
}

export function formatAsShortDate(date: Date | null) {
  if (!date) return "";
  const month = date.getMonth() + 1; // getMonth() returns 0-based month
  const day = date.getDate();
  const year = date.getFullYear() % 100; // get last two digits

  return `${month}/${day}/${year}`;
}

export function formatAsShortDateTime(date: Date | null) {
  if (!date) return "";
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear() % 100;

  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  hours = hours ? hours : 12; // convert 0 to 12

  const paddedMinutes = minutes.toString().padStart(2, "0");

  return `${month}/${day}/${year} ${hours}:${paddedMinutes} ${ampm}`;
}

export function getTimeDifference(date1: Date, date2: Date): { hours: number; minutes: number; seconds: number } {
  let diffInSeconds = Math.abs((date2.getTime() - date1.getTime()) / 1000);

  const hours = Math.floor(diffInSeconds / 3600);
  diffInSeconds %= 3600;

  const minutes = Math.floor(diffInSeconds / 60);
  const seconds = Math.floor(diffInSeconds % 60);

  return { hours, minutes, seconds };
}

export function calculateDateDifference(startDate: Date, endDate: Date): DateDifference {
  if (endDate < startDate) {
    [startDate, endDate] = [endDate, startDate]; // Swap to ensure start <= end
  }

  let years = endDate.getFullYear() - startDate.getFullYear();
  let months = endDate.getMonth() - startDate.getMonth();
  let days = endDate.getDate() - startDate.getDate();

  // Adjust for negative day difference
  if (days < 0) {
    months -= 1;
    const previousMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 0);
    days += previousMonth.getDate();
  }

  // Adjust for negative month difference
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  return {
    years,
    months,
    days,
  };
}

export function describeDateDifference(difference: DateDifference) {
  const parts = [] as string[];
  if (difference.years > 0) {
    const unit = difference.years === 1 ? "year" : "years";
    parts.push(`${difference.years} ${unit}`);
  }
  if (difference.months > 0) {
    const unit = difference.months === 1 ? "month" : "months";
    parts.push(`${difference.months} ${unit}`);
  }
  if (difference.days > 0) {
    const unit = difference.days === 1 ? "day" : "days";
    parts.push(`${difference.days} ${unit}`);
  }

  if (!parts.length) {
    return "0 days";
  }

  if (parts.length === 1) {
    return parts[0];
  }

  if (parts.length === 2) {
    return `${parts[0]} and ${parts[1]}`;
  }

  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
}

export function buildDateTime(baseDate: Dayjs, timeString: string) {
  // Parse time string in 12-hour format with AM/PM
  // Input like "12:30pm" or "12:30" with separate ampm parameter
  const time = dayjs(timeString, "hh:mma");
  if (!time.isValid()) {
    // Fallback if parsing failed
    return baseDate.hour(0).minute(0).second(0).millisecond(0);
  }
  // Return datetime with time applied - dayjs stores as local time when using hour/minute
  return baseDate.hour(time.hour()).minute(time.minute()).second(0).millisecond(0);
}

export function buildDateTimeFromInferredLocalTime(baseDate: Dayjs, timeString: string) {
  const trimmed = timeString.trim();
  const invalidDate = dayjs("invalid");

  const twelveHourMatch = trimmed.match(/^(1[0-2]|0?[1-9]):([0-5]\d)(?::([0-5]\d))?\s*([aApP][mM])$/);
  if (twelveHourMatch) {
    const hour12 = Number(twelveHourMatch[1]);
    const minute = Number(twelveHourMatch[2]);
    const second = Number(twelveHourMatch[3] ?? "0");
    const meridiem = twelveHourMatch[4].toLowerCase();

    let hour = hour12 % 12;
    if (meridiem === "pm") {
      hour += 12;
    }

    return baseDate.hour(hour).minute(minute).second(second).millisecond(0);
  }

  const localTimeMatch = trimmed.match(/^(\d{1,2}):([0-5]\d)(?::([0-5]\d))?$/);
  if (!localTimeMatch) {
    return invalidDate;
  }

  const hour = Number(localTimeMatch[1]);
  const minute = Number(localTimeMatch[2]);
  const second = Number(localTimeMatch[3] ?? "0");
  const hasInvalidHour = Number.isNaN(hour) || hour < 0 || hour > 23;
  const hasInvalidMinute = Number.isNaN(minute) || minute < 0 || minute > 59;
  if (hasInvalidHour || hasInvalidMinute) {
    return invalidDate;
  }

  // 0:xx and 13:xx-23:xx are explicit 24-hour inputs.
  if (hour === 0 || hour > 12) {
    return baseDate.hour(hour).minute(minute).second(second).millisecond(0);
  }

  // Inference rule: 12:00-6:00 => PM, 6:01-11:59 => AM.
  const isPm = hour === 12 || hour < 6 || (hour === 6 && minute === 0);
  let normalizedHour = hour % 12;
  if (isPm) {
    normalizedHour += 12;
  }

  return baseDate.hour(normalizedHour).minute(minute).second(second).millisecond(0);
}
